import { useState, type FormEvent } from "react";
import { ArrowRight, FileText, LifeBuoy, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { ListSkeleton, TableSkeleton } from "@/components/shared/Skeletons";
import { Field } from "@/components/forms";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  TICKET_CATEGORIES,
  TICKET_STATUS,
  categoryLabel,
  useCreateTicket,
  useMyTicket,
  useMyTickets,
  useReplyToMyTicket,
  useSupportFaqs,
  type NewTicket,
} from "@/hooks/useSupport";
import { formatDate } from "@/utils/formatters";
import { TicketConversation } from "./TicketConversation";

const EMPTY_TICKET: NewTicket = {
  subject: "",
  category: "general",
  priority: "medium",
  description: "",
};

/** The student and partner help page: their tickets, a way to raise one, and the FAQ. */
export default function SupportCenter({ dashboardPath }: { dashboardPath: string }) {
  const [composeOpen, setComposeOpen] = useState(false);
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);

  const ticketsQuery = useMyTickets();
  const faqsQuery = useSupportFaqs();
  const tickets = ticketsQuery.data ?? [];
  const faqs = faqsQuery.data ?? [];

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Help & support"
        subtitle="Ask the Taskpart team for help and follow the answers to your questions."
        breadcrumbs={[{ label: "Dashboard", path: dashboardPath }, { label: "Support" }]}
        action={
          <Button onClick={() => setComposeOpen(true)}>
            <MessageSquarePlus />
            New ticket
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-[1.4fr_1fr]">
        <SectionCard title="My tickets" className="h-auto self-start" noPadding>
          {ticketsQuery.isLoading ? (
            <TableSkeleton rows={3} columns={3} />
          ) : ticketsQuery.isError ? (
            <EmptyState
              bare
              icon={<LifeBuoy />}
              title="Couldn't load your tickets"
              description="Check your connection and try again."
              actionLabel="Retry"
              onAction={() => ticketsQuery.refetch()}
            />
          ) : tickets.length === 0 ? (
            <EmptyState
              bare
              icon={<LifeBuoy />}
              title="No tickets yet"
              description="If something isn't working or you have a question, open a ticket and the support team will reply here."
              actionLabel="New ticket"
              onAction={() => setComposeOpen(true)}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-4 md:pl-5">Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-4 text-right md:pr-5">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id} className="h-11 [&>td]:py-1.5">
                    <TableCell className="min-w-[200px] pl-4 md:pl-5">
                      <p className="font-medium text-foreground">{ticket.subject}</p>
                      <p className="text-caption text-muted-foreground">
                        {categoryLabel(ticket.category)} ·{" "}
                        <span className="tabular-nums">{formatDate(ticket.created_at)}</span>
                        {ticket.message_count > 0 &&
                          ` · ${ticket.message_count} ${ticket.message_count === 1 ? "reply" : "replies"}`}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={TICKET_STATUS[ticket.status]?.variant ?? "outline"} dot>
                        {TICKET_STATUS[ticket.status]?.label ?? ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="pr-4 text-right md:pr-5">
                      <IconAction
                        label="Open ticket"
                        icon={<ArrowRight />}
                        onClick={() => setOpenTicketId(ticket.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        <SectionCard
          title="Common questions"
          description="Answers to what people ask most often."
          className="h-auto self-start"
        >
          {faqsQuery.isLoading ? (
            <ListSkeleton items={3} />
          ) : faqs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No articles have been published yet.</p>
          ) : (
            <Accordion type="single" collapsible className="-mt-2">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`faq-${faq.id}`} className="border-border">
                  <AccordionTrigger className="gap-3 text-left text-sm font-medium hover:no-underline">
                    <span className="flex items-center gap-2.5">
                      <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-[26px] leading-6 whitespace-pre-wrap text-body">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </SectionCard>
      </div>

      <NewTicketDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onCreated={(id) => setOpenTicketId(id)}
      />
      <TicketSheet ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
    </div>
  );
}

function NewTicketDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [form, setForm] = useState<NewTicket>(EMPTY_TICKET);
  const [touched, setTouched] = useState(false);
  const createTicket = useCreateTicket();

  const subjectError =
    touched && form.subject.trim().length < 4
      ? "Give the ticket a short title (at least 4 characters)."
      : undefined;
  const descriptionError =
    touched && form.description.trim().length < 10
      ? "Describe the problem in a sentence or two (at least 10 characters)."
      : undefined;

  const close = () => {
    onClose();
    setForm(EMPTY_TICKET);
    setTouched(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (form.subject.trim().length < 4 || form.description.trim().length < 10) return;
    try {
      const ticket = await createTicket.mutateAsync(form);
      toast.success("Ticket sent", { description: "The support team will reply here." });
      close();
      if (ticket?.id) onCreated(ticket.id);
    } catch {
      // Errors are already shown by the API client.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>New support ticket</DialogTitle>
            <DialogDescription>
              Tell us what's going on. You'll get a notification when support replies.
            </DialogDescription>
          </DialogHeader>

          <Field label="Subject" error={subjectError} required>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              maxLength={160}
              placeholder="e.g. I can't submit my work log"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Topic">
              {({ id }) => (
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v as NewTicket["category"] })}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="How urgent is it?">
              {({ id }) => (
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v as NewTicket["priority"] })}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low: a question</SelectItem>
                    <SelectItem value="medium">Medium: slowing me down</SelectItem>
                    <SelectItem value="high">High: I'm blocked</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>

          <Field label="Details" error={descriptionError} required>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={6}
              maxLength={5000}
              placeholder="What were you trying to do, and what happened instead?"
            />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={createTicket.isPending}>
              Send ticket
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TicketSheet({ ticketId, onClose }: { ticketId: number | null; onClose: () => void }) {
  const { data: ticket, isLoading } = useMyTicket(ticketId);
  const reply = useReplyToMyTicket(ticketId);

  return (
    <Sheet open={ticketId !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]">
        <SheetHeader className="space-y-1 border-b border-border px-6 py-5 text-left">
          <p className="eyebrow">Ticket #{ticketId}</p>
          <SheetTitle className="pr-6 text-xl font-normal">
            {isLoading || !ticket ? "Loading…" : ticket.subject}
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {ticket && (
              <>
                <Badge variant={TICKET_STATUS[ticket.status]?.variant ?? "outline"} dot>
                  {TICKET_STATUS[ticket.status]?.label ?? ticket.status}
                </Badge>
                <span>{categoryLabel(ticket.category)}</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading || !ticket ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <TicketConversation
              ticket={ticket}
              viewer="requester"
              onReply={(body) => reply.mutateAsync(body)}
              replying={reply.isPending}
              replyHint={
                ticket.status === "resolved"
                  ? "This ticket is resolved. Replying will reopen it."
                  : undefined
              }
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
